/**
 * Price Negotiation utilities.
 *
 * Allows buyers to propose a custom price when starting a transaction.
 * Seller can accept, reject, or counter-offer.
 *
 * Offer stored in transaction protectedData.priceOffer:
 * {
 *   amount: number (cents),
 *   currency: string,
 *   status: 'pending' | 'accepted' | 'rejected' | 'counter',
 *   counterAmount?: number (cents),
 *   message?: string,
 *   createdAt: ISO string,
 *   respondedAt?: ISO string
 * }
 */

export const OFFER_STATUSES = ['pending', 'accepted', 'rejected', 'counter'];

/**
 * Validate a price offer.
 * @param {{ amount: number|string, currency: string, originalPriceCents: number }} params
 * @returns {string|null} — error message or null
 */
export const validateOffer = ({ amount, currency, originalPriceCents }) => {
  const cents = Math.round(parseFloat(String(amount).replace(',', '.')) * 100);
  if (isNaN(cents)) return 'Invalid price';
  if (cents <= 0) return 'Price must be greater than 0';
  if (!currency) return 'Currency is required';

  // Optional: max offer cap (150% of original price not allowed)
  if (originalPriceCents && cents > originalPriceCents * 1.5) {
    return 'Offer cannot exceed 150% of the original price';
  }

  return null;
};

/**
 * Format an offer for display.
 * @param {number} amountCents
 * @param {string} currency
 * @returns {string}
 */
export const formatOffer = (amountCents, currency = 'EUR') => {
  const symbols = { EUR: '€', USD: '$', GBP: '£' };
  const symbol = symbols[currency] || currency + ' ';
  return `${symbol}${(amountCents / 100).toFixed(2)}`;
};

/**
 * Calculate discount percentage between offer and original price.
 * @param {number} offerCents
 * @param {number} originalCents
 * @returns {number} — percentage (negative = discount, positive = premium)
 */
export const calculateOfferDiff = (offerCents, originalCents) => {
  if (!originalCents) return 0;
  return Math.round(((offerCents - originalCents) / originalCents) * 100);
};

/**
 * Get current offer from a transaction.
 * @param {Object|null} transaction
 * @returns {Object|null}
 */
export const getOffer = (transaction) =>
  transaction?.attributes?.protectedData?.priceOffer || null;

/**
 * Check if an offer is pending.
 */
export const isOfferPending = (transaction) =>
  getOffer(transaction)?.status === 'pending';
